import { useState, useEffect } from 'react';
import { Search, ExternalLink, Check, X, AlertCircle, RefreshCw, Database, FileSpreadsheet } from 'lucide-react';
import { OpsLayout } from '@/components/ops/OpsLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useBWCFeeSchedule } from '@/hooks/useBWCFeeSchedule';
import { COVERAGE_FLAGS, type BWCFeeValidationResult } from '@/lib/bwc/types';

const BWC_OFFICIAL_LOOKUP_URL = 'https://www.bwc.ohio.gov/provider/services/feelookup/default.asp';

export default function BWCFeeValidation() {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [validationResults, setValidationResults] = useState<BWCFeeValidationResult[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  
  const { 
    versions, 
    entries, 
    loading, 
    error, 
    fetchVersions, 
    fetchEntries,
    searchCPTCodes,
    validateAgainstOfficial 
  } = useBWCFeeSchedule();

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  const handleSearch = async () => {
    if (!searchTerm) return;
    
    // Parse search term - could be comma-separated CPT codes
    const codes = searchTerm.split(',').map(c => c.trim()).filter(Boolean);
    
    if (codes.length === 1 && codes[0].length < 5) {
      // Partial search for autocomplete
      const results = await searchCPTCodes(codes[0]);
      setSearchResults(results);
    } else {
      // Exact lookup
      await fetchEntries({ cpt_codes: codes });
      setSearchResults([]);
    }
  };

  const handleValidateCode = async (cptCode: string, modifier?: string) => {
    setIsValidating(true);
    try {
      const result = await validateAgainstOfficial(cptCode, modifier ?? undefined);
      setValidationResults(prev => {
        const existing = prev.findIndex(
          r => r.cpt_code === cptCode && r.modifier === modifier
        );
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = result;
          return updated;
        }
        return [...prev, result];
      });
    } finally {
      setIsValidating(false);
    }
  };

  const formatCurrency = (amount?: number | null) => {
    if (amount === null || amount === undefined) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getCoverageFlagBadge = (flag?: string | null) => {
    if (!flag) return null;
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      'BR': 'secondary',
      'NRC': 'outline',
      'NC': 'destructive',
      'PA': 'default',
    };
    return (
      <Badge variant={variants[flag] || 'outline'} className="text-xs">
        {flag}
      </Badge>
    );
  };

  const getMatchStatusIcon = (status: BWCFeeValidationResult['match_status']) => {
    switch (status) {
      case 'match':
        return <Check className="h-4 w-4 text-primary" />;
      case 'mismatch':
        return <X className="h-4 w-4 text-red-500" />;
      case 'pending':
        return <AlertCircle className="h-4 w-4 text-amber-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  return (
    <OpsLayout>
      <div className="w-full max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight">BWC Fee Validation</h1>
          <p className="text-muted-foreground mt-2">
            Query internal fee schedule data and validate against the official Ohio BWC fee lookup.
          </p>
        </div>

        {/* Version Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {versions.map(version => (
            <Card key={version.id} className={version.is_active ? 'border-primary' : ''}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Version {version.version}</CardTitle>
                  {version.is_active && (
                    <Badge variant="default">Active</Badge>
                  )}
                </div>
                <CardDescription>
                  Effective: {new Date(version.effective_date).toLocaleDateString()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">{version.source_document}</p>
              </CardContent>
            </Card>
          ))}
          {versions.length === 0 && !loading && (
            <Card className="col-span-3">
              <CardContent className="py-8 text-center text-muted-foreground">
                <Database className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No fee schedule versions loaded. Import the BWC fee schedule data to get started.</p>
              </CardContent>
            </Card>
          )}
        </div>

        <Tabs defaultValue="lookup" className="space-y-6">
          <TabsList>
            <TabsTrigger value="lookup">Fee Lookup</TabsTrigger>
            <TabsTrigger value="validation">Spot-Check Validation</TabsTrigger>
            <TabsTrigger value="browse">Browse Schedule</TabsTrigger>
          </TabsList>

          {/* Fee Lookup Tab */}
          <TabsContent value="lookup" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="h-5 w-5" />
                  CPT Code Lookup
                </CardTitle>
                <CardDescription>
                  Enter CPT codes (comma-separated for multiple) to look up BWC fee amounts.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter CPT code(s), e.g., 27447, 29881"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="flex-1"
                  />
                  <Button onClick={handleSearch} disabled={loading}>
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    Search
                  </Button>
                </div>

                {/* Autocomplete results */}
                {searchResults.length > 0 && (
                  <div className="border rounded-lg divide-y">
                    {searchResults.map((result, i) => (
                      <button
                        key={i}
                        className="w-full px-4 py-2 text-left hover:bg-accent flex items-center justify-between"
                        onClick={() => {
                          setSearchTerm(result.cpt_code);
                          setSearchResults([]);
                          fetchEntries({ cpt_codes: [result.cpt_code] });
                        }}
                      >
                        <span className="font-mono">{result.cpt_code}</span>
                        <span className="text-muted-foreground text-sm">
                          {formatCurrency(result.non_facility_fee)} / {formatCurrency(result.facility_fee)}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Results table */}
                {entries.length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>CPT</TableHead>
                        <TableHead>Mod</TableHead>
                        <TableHead>Mod 2</TableHead>
                        <TableHead>Coverage</TableHead>
                        <TableHead className="text-right">Non-Facility</TableHead>
                        <TableHead className="text-right">Facility</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entries.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell className="font-mono font-medium">{entry.cpt_code}</TableCell>
                          <TableCell>{entry.modifier || '—'}</TableCell>
                          <TableCell>{entry.modifier_2 || '—'}</TableCell>
                          <TableCell>{getCoverageFlagBadge(entry.coverage_flag)}</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(entry.non_facility_fee)}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(entry.facility_fee)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleValidateCode(entry.cpt_code, entry.modifier ?? undefined)}
                              disabled={isValidating}
                            >
                              Validate
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Validation Tab */}
          <TabsContent value="validation" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ExternalLink className="h-5 w-5" />
                  Official BWC Fee Lookup
                </CardTitle>
                <CardDescription>
                  Use the official Ohio BWC fee lookup tool for spot-check validation of internal data.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Manual Validation Required</p>
                      <p className="text-sm text-muted-foreground">
                        Due to the nature of the BWC official lookup tool, automated validation requires 
                        manual cross-reference. Click the button below to open the official fee lookup 
                        in a new window.
                      </p>
                      <Button asChild variant="outline" className="mt-2">
                        <a href={BWC_OFFICIAL_LOOKUP_URL} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Open BWC Fee Lookup
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Validation results */}
                {validationResults.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium">Validation Queue</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>CPT</TableHead>
                          <TableHead>Modifier</TableHead>
                          <TableHead className="text-right">Internal Non-Fac</TableHead>
                          <TableHead className="text-right">Internal Fac</TableHead>
                          <TableHead className="text-right">Official Non-Fac</TableHead>
                          <TableHead className="text-right">Official Fac</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {validationResults.map((result, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-mono">{result.cpt_code}</TableCell>
                            <TableCell>{result.modifier || '—'}</TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(result.internal_non_facility_fee)}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(result.internal_facility_fee)}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(result.official_non_facility_fee)}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(result.official_facility_fee)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {getMatchStatusIcon(result.match_status)}
                                <span className="text-xs capitalize">{result.match_status.replace('_', ' ')}</span>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Browse Tab */}
          <TabsContent value="browse" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5" />
                  Browse Fee Schedule
                </CardTitle>
                <CardDescription>
                  Browse the complete fee schedule data. Note: CPT descriptions are not stored due to 
                  AMA licensing requirements.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 mb-4">
                  <Button variant="outline" onClick={() => fetchEntries({})}>
                    Load First 500 Entries
                  </Button>
                </div>

                {entries.length > 0 && (
                  <>
                    <p className="text-sm text-muted-foreground mb-4">
                      Showing {entries.length} entries
                    </p>
                    <div className="max-h-[600px] overflow-auto border rounded-lg">
                      <Table>
                        <TableHeader className="sticky top-0 bg-background">
                          <TableRow>
                            <TableHead>CPT</TableHead>
                            <TableHead>Mod</TableHead>
                            <TableHead>Coverage</TableHead>
                            <TableHead className="text-right">Non-Facility</TableHead>
                            <TableHead className="text-right">Facility</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {entries.map((entry) => (
                            <TableRow key={entry.id}>
                              <TableCell className="font-mono">{entry.cpt_code}</TableCell>
                              <TableCell>{entry.modifier || '—'}</TableCell>
                              <TableCell>{getCoverageFlagBadge(entry.coverage_flag)}</TableCell>
                              <TableCell className="text-right font-mono">
                                {formatCurrency(entry.non_facility_fee)}
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                {formatCurrency(entry.facility_fee)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </>
                )}

                {entries.length === 0 && !loading && (
                  <div className="text-center py-12 text-muted-foreground">
                    <Database className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Click "Load First 500 Entries" to browse the fee schedule.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Coverage Flag Legend */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Coverage Flag Legend</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(COVERAGE_FLAGS).map(([flag, description]) => (
                    <div key={flag} className="flex items-start gap-3">
                      {getCoverageFlagBadge(flag)}
                      <span className="text-sm text-muted-foreground">{description}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Error display */}
        {error && (
          <div className="mt-4 bg-destructive/10 border border-destructive/30 rounded-lg p-4 text-destructive">
            {error}
          </div>
        )}
      </div>
    </OpsLayout>
  );
}
